import { DateTime } from "luxon";

import type { PnrDisplaySegment, PnrDisplayView } from "../../api/use-claim-pnr-display";

export type SegmentTone = "ok" | "warn" | "critical" | "neutral";

export type SegmentStatusFamily = "Confirmed" | "Proposal" | "Cancelled" | "WaitingList";

/**
 * Amadeus action codes grouped the way the API reads them — a mirror of `SegmentStatusEnum` in
 * `apps/api/src/modules/GDS/amadeus/lib/parse-pnr-history/types/segment-status.ts`, which the
 * SDK does not export. Keep the two in step.
 */
// biome-ignore-start lint/style/useNamingConvention: Amadeus codes as keys
export const SEGMENT_STATUS_FAMILY: Record<string, SegmentStatusFamily> = {
  HX: "Cancelled",
  UN: "Cancelled",
  UC: "Cancelled",
  NO: "Cancelled",
  XS: "Cancelled",
  XC: "Cancelled",
  IX: "Cancelled",
  LK: "Proposal",
  TK: "Proposal",
  TL: "Proposal",
  GL: "Proposal",
  NN: "Proposal",
  RQ: "Proposal",
  WL: "Proposal",
  SS: "Proposal",
  IS: "Proposal",
  IN: "Proposal",
  LL: "WaitingList",
  PE: "WaitingList",
  KL: "WaitingList",
  HL: "WaitingList",
  RR: "Confirmed",
  DK: "Confirmed",
  HS: "Confirmed",
  HK: "Confirmed",
  NK: "Confirmed",
  KK: "Confirmed",
  GK: "Confirmed",
};

/** Short wording shown next to the code in the status pill (`HK · confirmé`). */
export const SEGMENT_STATUS_SHORT: Record<string, string> = {
  HK: "confirmé",
  KK: "confirmé",
  RR: "reconfirmé",
  HS: "vendu",
  DK: "confirmé",
  NK: "confirmé",
  GK: "segment passif",
  TK: "horaire modifié",
  TL: "horaire modifié",
  NN: "demandé",
  SS: "vendu",
  LK: "confirmé hors inventaire",
  RQ: "demande en cours",
  WL: "annulé par la compagnie",
  IS: "vendu",
  IN: "demandé",
  GL: "segment fantôme",
  HL: "liste d'attente",
  KL: "confirmé",
  LL: "liste d'attente",
  PE: "liste d'attente",
  UN: "annulé par la compagnie",
  HX: "annulé",
  UC: "refusé",
  NO: "non confirmé",
  XS: "annulé",
  XC: "annulé",
  IX: "annulé",
};

/** Finer wording per code, for the tooltip behind the status pill. */
export const SEGMENT_STATUS_DETAIL: Record<string, string> = {
  HK: "Confirmé",
  KK: "Confirmé par la compagnie",
  RR: "Reconfirmé",
  HS: "Vendu",
  DK: "Confirmé (vente directe)",
  NK: "Confirmé (hors BSP)",
  GK: "Segment passif",
  TK: "Horaire modifié, à reconfirmer",
  TL: "Horaire modifié, liste d'attente",
  NN: "Demandé",
  SS: "Vendu, en attente de confirmation",
  LK: "Confirmé hors inventaire",
  RQ: "Demande en cours",
  WL: "Annulé par la compagnie (était en liste d'attente)",
  IS: "Vendu (informatif)",
  IN: "Demandé (informatif)",
  GL: "Segment fantôme",
  HL: "Liste d'attente",
  KL: "Confirmé depuis la liste d'attente",
  LL: "Liste d'attente",
  PE: "Liste d'attente prioritaire",
  UN: "Annulé par la compagnie",
  HX: "Annulé par la compagnie",
  UC: "Refusé par la compagnie",
  NO: "Sans action de la compagnie",
  XS: "Annulé",
  XC: "Annulé",
  IX: "Annulé (informatif)",
};
// biome-ignore-end lint/style/useNamingConvention: Amadeus codes as keys

// biome-ignore-start lint/style/useNamingConvention: enum-valued keys
export const SEGMENT_STATUS_FAMILY_LABEL: Record<SegmentStatusFamily, { label: string; tone: SegmentTone }> =
  {
    Confirmed: { label: "Confirmé", tone: "ok" },
    Proposal: { label: "Proposé", tone: "neutral" },
    Cancelled: { label: "Annulé", tone: "critical" },
    WaitingList: { label: "Liste d'attente", tone: "warn" },
  };
// biome-ignore-end lint/style/useNamingConvention: enum-valued keys

/** `TK` is the one Proposal code worth an amber pill: the airline changed the schedule. */
export function segmentStatusTone(code: string): SegmentTone {
  if (code === "TK" || code === "TL") {
    return "warn";
  }

  const family = SEGMENT_STATUS_FAMILY[code];

  return family ? SEGMENT_STATUS_FAMILY_LABEL[family].tone : "neutral";
}

// biome-ignore-start lint/style/useNamingConvention: Amadeus codes as keys
/** E-ticket coupon statuses of the PNR's `FA` element. Unlisted codes show raw. */
export const COUPON_STATUS_LABELS: Record<string, string> = {
  O: "ouvert, non utilisé",
  A: "contrôle aéroport",
  C: "enregistré",
  L: "embarqué",
  B: "vol effectué",
  E: "échangé",
  R: "remboursé",
  V: "annulé",
  I: "émission d'origine",
  S: "suspendu",
};

/** `SSR DOCS` gender; `I` marks an infant. */
export const GENDER_LABELS: Record<string, string> = {
  M: "Homme",
  F: "Femme",
  MI: "Homme (bébé)",
  FI: "Femme (bébé)",
  U: "Non précisé",
  X: "Non précisé",
};

export const CABIN_LABELS: Record<string, string> = {
  Y: "Économique",
  M: "Économique",
  W: "Premium",
  C: "Affaires",
  J: "Affaires",
  F: "Première",
  P: "Première",
};

export const EQUIPMENT_LABELS: Record<string, string> = {
  "319": "Airbus A319",
  "320": "Airbus A320",
  "32A": "Airbus A320",
  "32N": "Airbus A320neo",
  "321": "Airbus A321",
  "32Q": "Airbus A321neo",
  "330": "Airbus A330",
  "332": "Airbus A330-200",
  "333": "Airbus A330-300",
  "339": "Airbus A330-900neo",
  "350": "Airbus A350",
  "351": "Airbus A350-1000",
  "359": "Airbus A350-900",
  "380": "Airbus A380",
  "388": "Airbus A380-800",
  "737": "Boeing 737",
  "738": "Boeing 737-800",
  "73H": "Boeing 737-800",
  "7M8": "Boeing 737 MAX 8",
  "744": "Boeing 747-400",
  "763": "Boeing 767-300",
  "772": "Boeing 777-200",
  "77L": "Boeing 777-200LR",
  "773": "Boeing 777-300",
  "77W": "Boeing 777-300ER",
  "787": "Boeing 787",
  "788": "Boeing 787-8",
  "789": "Boeing 787-9",
  "78J": "Boeing 787-10",
  E90: "Embraer 190",
  E95: "Embraer 195",
  E7W: "Embraer 175",
  CR9: "Bombardier CRJ-900",
  AT7: "ATR 72",
  AT5: "ATR 42",
};

/** Non-flight itinerary elements, as Amadeus names them. */
export const ELEMENT_TYPE_LABELS: Record<string, string> = {
  RU: "Rappel agence",
  MIS: "Divers",
  TUR: "Tour",
  OTH: "Autre",
  ARNK: "Trajet de surface",
  BUS: "Bus",
  FER: "Ferry",
};

/** Ground segments Amadeus books alongside the flights — shown inside the stay they belong to. */
export const GROUND_TYPE_LABELS: Record<string, string> = {
  CCR: "Location de voiture",
  HHL: "Hôtel",
  TRN: "Train",
};

export const CAR_RENTAL_COMPANIES: Record<string, string> = {
  EP: "Europcar",
  ZE: "Hertz",
  ZI: "Avis",
  ZD: "Budget",
  ZR: "Budget",
  SX: "Sixt",
  ET: "Enterprise",
  ZL: "National",
  AL: "Alamo",
  ZT: "Thrifty",
};
// biome-ignore-end lint/style/useNamingConvention: Amadeus codes as keys

/** What flight monitoring knows about a flight of the reservation, keyed by `flightKey`. */
export interface FlightMonitoring {
  monitoringStatus: string | null;
  /** ISO instants (UTC). */
  actualDepartureUtc: string | null;
  actualArrivalUtc: string | null;
}

/** Airports, airlines and monitored flights the PNR codes resolve to — read from the reservation. */
export interface PnrReference {
  airports: Record<
    string,
    {
      name: string;
      nameFr: string | null;
      city: string | null;
      cityFr: string | null;
      timezone: string | null;
    }
  >;
  airlines: Record<string, { name: string; iconUrl: string | null }>;
  flights: Record<string, FlightMonitoring>;
}

export const EMPTY_REFERENCE: PnrReference = { airports: {}, airlines: {}, flights: {} };

/** `AF342|2026-09-07` — carrier, number without leading zeros, scheduled local departure date. */
export function flightKey(
  carrier: string | null,
  number: string | null,
  localDate: string | null,
): string | null {
  if (!carrier || !number || !localDate) {
    return null;
  }

  return `${carrier}${Number.parseInt(number, 10)}|${localDate.split("T")[0]}`;
}

export function segmentFlightKey(segment: PnrDisplaySegment): string | null {
  return flightKey(segment.carrier, segment.flightNumber, segment.departureAt);
}

export function airportCity(iata: string | null, reference: PnrReference): string {
  const airport = iata ? reference.airports[iata] : undefined;

  return airport?.cityFr ?? airport?.city ?? iata ?? "—";
}

export function airportName(iata: string | null, reference: PnrReference): string | null {
  const airport = iata ? reference.airports[iata] : undefined;
  // The reservation names airports "Nice Côte d'Azur Airport, Nice (NCE)" — the code is shown apart.
  return (airport?.nameFr ?? airport?.name)?.replace(/\s*\([A-Z]{3}\)\s*$/, "") ?? null;
}

/** A stop of a day or more is a stay, not a connection: it opens a new journey. */
export const JOURNEY_BREAK_MINUTES = 24 * 60;

export const LONG_LAYOVER_MINUTES = 4 * 60;

export interface Layover {
  airport: string | null;
  nextAirport: string | null;
  /** Null when either clock is missing. */
  minutes: number | null;
  airportChange: boolean;
  isLong: boolean;
}

export interface Journey {
  label: string;
  from: string | null;
  to: string | null;
  departureAt: string | null;
  arrivalAt: string | null;
  segments: PnrDisplaySegment[];
  /** `layovers[i]` sits between `segments[i]` and `segments[i + 1]`. */
  layovers: Layover[];
}

export interface Stay {
  airport: string | null;
  nextAirport: string | null;
  minutes: number;
  /** Car, hotel and train segments booked during the stay. */
  ground: PnrDisplaySegment[];
}

export type TripType = "Aller simple" | "Aller-retour" | "Multi-destinations";

export interface ItineraryGroups {
  tripType: TripType;
  journeys: Journey[];
  /** `stays[i]` separates `journeys[i]` from `journeys[i + 1]`. */
  stays: Stay[];
  cancelled: PnrDisplaySegment[];
  others: PnrDisplaySegment[];
}

/**
 * Amadeus clocks are local with no offset. A connection is at one airport, so both clocks share a
 * timezone and the layover is exact; a flight duration needs both airports' timezones, which the
 * reservation provides — see `flightMinutes`.
 */
export function naiveToUtcDate(value: string | null): Date | null {
  if (!value) {
    return null;
  }

  const [date, time = "00:00"] = value.split("T");
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);

  if ([year, month, day, hours, minutes].some(Number.isNaN)) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, day, hours, minutes));
}

export function minutesBetween(from: string | null, to: string | null): number | null {
  const start = naiveToUtcDate(from);
  const end = naiveToUtcDate(to);

  if (!start || !end) {
    return null;
  }

  return Math.round((end.getTime() - start.getTime()) / 60_000);
}

/** Real elapsed time between two local clocks, once each airport's timezone is known. */
export function elapsedMinutes(
  departureAt: string | null,
  departureAirport: string | null,
  arrivalAt: string | null,
  arrivalAirport: string | null,
  reference: PnrReference,
): number | null {
  const departureZone = departureAirport ? reference.airports[departureAirport]?.timezone : null;
  const arrivalZone = arrivalAirport ? reference.airports[arrivalAirport]?.timezone : null;

  if (!departureAt || !arrivalAt || !departureZone || !arrivalZone) {
    return null;
  }

  const departure = DateTime.fromISO(departureAt, { zone: departureZone });
  const arrival = DateTime.fromISO(arrivalAt, { zone: arrivalZone });

  if (!departure.isValid || !arrival.isValid) {
    return null;
  }

  return Math.round(arrival.diff(departure, "minutes").minutes);
}

export function formatDuration(totalMinutes: number): string {
  const sign = totalMinutes < 0 ? "-" : "";
  const absolute = Math.abs(totalMinutes);
  const days = Math.floor(absolute / 1440);
  const hours = Math.floor((absolute % 1440) / 60);
  const minutes = absolute % 60;

  if (days > 0) {
    return `${sign}${days} j${hours > 0 ? ` ${hours} h` : ""}`;
  }
  if (hours > 0) {
    return `${sign}${hours} h${minutes > 0 ? ` ${String(minutes).padStart(2, "0")}` : ""}`;
  }

  return `${sign}${minutes} min`;
}

export function formatNaiveDate(
  value: string | null,
  options: { weekday?: boolean; year?: boolean } = {},
): string {
  const date = naiveToUtcDate(value);
  if (!date) {
    return "—";
  }

  return date.toLocaleDateString("fr-FR", {
    weekday: options.weekday ? "short" : undefined,
    day: "numeric",
    month: "long",
    year: options.year ? "numeric" : undefined,
    timeZone: "UTC",
  });
}

export function formatNaiveTime(value: string | null): string {
  return value?.split("T")[1] ?? "—";
}

/** `7 – 11 septembre 2026`, or `28 août – 2 septembre 2026` across two months. */
export function formatDateRange(from: string | null, to: string | null): string {
  const start = naiveToUtcDate(from);
  const end = naiveToUtcDate(to);

  if (!start || !end) {
    return formatNaiveDate(from ?? to, { year: true });
  }

  const sameMonth =
    start.getUTCFullYear() === end.getUTCFullYear() && start.getUTCMonth() === end.getUTCMonth();
  if (start.getTime() === end.getTime() || (sameMonth && start.getUTCDate() === end.getUTCDate())) {
    return formatNaiveDate(from, { year: true });
  }
  if (sameMonth) {
    return `${start.getUTCDate()} – ${formatNaiveDate(to, { year: true })}`;
  }

  return `${formatNaiveDate(from)} – ${formatNaiveDate(to, { year: true })}`;
}

export function isFlight(segment: PnrDisplaySegment): boolean {
  return segment.type ? segment.type === "AIR" : Boolean(segment.to);
}

export function isGround(segment: PnrDisplaySegment): boolean {
  return segment.type !== null && segment.type in GROUND_TYPE_LABELS;
}

/** The first code is the segment action code; the ones after it are e-ticket coupon statuses. */
export function segmentStatusCode(segment: PnrDisplaySegment): string | null {
  return segment.statuses[0] ?? null;
}

export function couponStatusCodes(segment: PnrDisplaySegment): string[] {
  return segment.statuses.slice(1);
}

export function isCancelled(segment: PnrDisplaySegment): boolean {
  const code = segmentStatusCode(segment);

  return code !== null && SEGMENT_STATUS_FAMILY[code] === "Cancelled";
}

type Leg = Pick<Journey, "from" | "to">;

function tripTypeOf(journeys: Leg[]): TripType {
  if (journeys.length <= 1) {
    return "Aller simple";
  }

  const [outbound] = journeys;
  const last = journeys[journeys.length - 1];
  if (journeys.length === 2 && last.to === outbound.from) {
    return "Aller-retour";
  }

  return "Multi-destinations";
}

function journeyLabels(journeys: Leg[], tripType: TripType): string[] {
  if (tripType === "Aller-retour") {
    return ["Aller", "Retour"];
  }
  if (journeys.length <= 1) {
    return ["Aller"];
  }

  return journeys.map((_journey, index) => `Tronçon ${index + 1}`);
}

/**
 * Chains the active flights into journeys: consecutive flights separated by less than a day form
 * one journey, a longer stop is a stay between two journeys. Ground segments are filed under the
 * stay they overlap; cancelled flights and the remaining elements (retention lines, tours…) are
 * set aside so they never create a bogus layover.
 */
export function groupItinerary(segments: PnrDisplaySegment[]): ItineraryGroups {
  const ground = segments.filter(isGround);
  const others = segments.filter((segment) => !isFlight(segment) && !isGround(segment));
  const flights = segments.filter(isFlight);
  const cancelled = flights.filter(isCancelled);
  const active = flights
    .filter((segment) => !isCancelled(segment))
    .sort((a, b) => (a.departureAt ?? "").localeCompare(b.departureAt ?? "") || a.line - b.line);

  const journeys: Journey[] = [];
  const stays: Stay[] = [];

  for (const segment of active) {
    const current = journeys[journeys.length - 1];
    const previous = current?.segments[current.segments.length - 1];
    const minutes = previous ? minutesBetween(previous.arrivalAt, segment.departureAt) : null;
    const breaks = minutes !== null && minutes >= JOURNEY_BREAK_MINUTES;

    if (previous && breaks) {
      stays.push({
        airport: previous.to,
        nextAirport: segment.from,
        minutes,
        ground: ground.filter(
          (item) =>
            (item.departureAt ?? "") <= (segment.departureAt ?? "") &&
            (item.arrivalAt ?? item.departureAt ?? "") >= (previous.arrivalAt ?? ""),
        ),
      });
    }

    if (!current || !previous || breaks) {
      journeys.push({
        label: "",
        from: segment.from,
        to: segment.to,
        departureAt: segment.departureAt,
        arrivalAt: segment.arrivalAt,
        segments: [segment],
        layovers: [],
      });
      continue;
    }

    current.layovers.push({
      airport: previous.to,
      nextAirport: segment.from,
      minutes,
      airportChange: previous.to !== segment.from,
      isLong: minutes !== null && minutes >= LONG_LAYOVER_MINUTES,
    });
    current.segments.push(segment);
    current.to = segment.to;
    current.arrivalAt = segment.arrivalAt;
  }

  const tripType = tripTypeOf(journeys);
  const labels = journeyLabels(journeys, tripType);
  const filed = new Set(stays.flatMap((stay) => stay.ground));

  return {
    tripType,
    journeys: journeys.map((journey, index) => ({ ...journey, label: labels[index] })),
    stays,
    cancelled,
    // A ground segment outside every stay still has to show somewhere.
    others: [...others, ...ground.filter((item) => !filed.has(item))],
  };
}

export interface SegmentNote {
  text: string;
  raw: string;
}

const ETD_CHANGE =
  /TRAVELLER-(EMAIL|SMS)-ETDCHANGE FOR ([A-Z0-9]{2})\s?(\d{1,4}[A-Z]?)\b.*?ON (\d{2})\/(\d{2})\/(\d{4}) AT (\d{2})(\d{2})/;

/**
 * What the PNR says about this flight outside the segment line: the agency's schedule-change
 * notifications (`RM … ETDCHANGE FOR XK201 …`) and the carrier's `SSR OTHS` remarks that name the
 * flight or its aircraft.
 */
export function segmentNotes(
  segment: PnrDisplaySegment,
  rawElements: PnrDisplayView["rawElements"],
): SegmentNote[] {
  const flight = `${segment.carrier ?? ""}${segment.flightNumber ?? ""}`;
  const notes: SegmentNote[] = [];

  const changes = rawElements
    .filter((element) => element.name === "RM")
    .map((element) => ({ element, match: element.text.match(ETD_CHANGE) }))
    .filter(({ match }) => match !== null && `${match[2]}${match[3]}` === flight);

  if (changes.length > 0) {
    const [{ match, element }] = changes;
    const channels = [...new Set(changes.map(({ match: m }) => (m?.[1] === "SMS" ? "SMS" : "email")))];
    const when = match ? `${match[6]}-${match[5]}-${match[4]}T${match[7]}:${match[8]}` : null;

    notes.push({
      text: `Changement d'heure de départ notifié au passager${when ? ` le ${formatNaiveDate(when)} à ${formatNaiveTime(when)}` : ""} (${channels.join(" + ")})`,
      raw: element.text,
    });
  }

  const equipmentFamily = segment.equipment?.slice(0, 2) ?? null;
  const remarks = rawElements.filter(
    (element) =>
      element.name === "SSR" &&
      element.text.startsWith("OTHS") &&
      (element.text.includes(flight) ||
        (segment.equipment !== null && element.text.includes(segment.equipment)) ||
        (equipmentFamily === "AT" && element.text.includes("ATR"))),
  );

  if (remarks.length > 0) {
    notes.push({
      text: `Remarques compagnie (SSR OTHS) : ${remarks
        .map((element) => `« ${element.text.replace(/^OTHS\s+[A-Z0-9]{2}\s+/, "")} »`)
        .join(" · ")}`,
      raw: remarks.map((element) => element.text).join(" | "),
    });
  }

  return notes;
}

export interface ActualTime {
  /** The instant, ISO UTC. */
  at: string;
  /** Local clock at the airport, or UTC when its timezone is unknown. */
  local: string;
  isUtc: boolean;
  /** Minutes against the schedule; null when it cannot be measured. */
  delayMinutes: number | null;
}

export interface ActualTimes {
  source: "monitoring" | "pnr";
  cancelled: boolean;
  departure: ActualTime | null;
  arrival: ActualTime | null;
}

function actualTime(
  actualUtc: string | null,
  scheduledLocal: string | null,
  airport: string | null,
  reportedDelay: number | null,
  reference: PnrReference,
): ActualTime | null {
  if (!actualUtc) {
    return null;
  }

  const zone = airport ? reference.airports[airport]?.timezone : null;
  const actual = DateTime.fromISO(actualUtc, { zone: "utc" });
  if (!actual.isValid) {
    return null;
  }

  const at = actual.toISO() ?? actualUtc;
  if (!zone) {
    return { at, local: actual.toFormat("HH:mm"), isUtc: true, delayMinutes: reportedDelay };
  }

  const localActual = actual.setZone(zone);
  const scheduled = scheduledLocal ? DateTime.fromISO(scheduledLocal, { zone }) : null;
  const delayMinutes =
    scheduled?.isValid === true ? Math.round(localActual.diff(scheduled, "minutes").minutes) : reportedDelay;

  return { at, local: localActual.toFormat("HH:mm"), isUtc: false, delayMinutes };
}

/**
 * What actually happened to a flight. Flight monitoring wins when it followed the flight; the
 * PNR's own `FDL` lines are the fallback, with the delay the airline announced when the clocks
 * cannot be compared.
 */
export function actualTimes(segment: PnrDisplaySegment, reference: PnrReference): ActualTimes | null {
  const key = segmentFlightKey(segment);
  const monitored = key ? reference.flights[key] : undefined;

  if (
    monitored &&
    (monitored.monitoringStatus || monitored.actualDepartureUtc || monitored.actualArrivalUtc)
  ) {
    return {
      source: "monitoring",
      cancelled: monitored.monitoringStatus === "CANCELLED",
      departure: actualTime(monitored.actualDepartureUtc, segment.departureAt, segment.from, null, reference),
      arrival: actualTime(monitored.actualArrivalUtc, segment.arrivalAt, segment.to, null, reference),
    };
  }

  const { flightData } = segment;
  if (!flightData.actualDepartureUtc && !flightData.actualArrivalUtc) {
    return null;
  }

  return {
    source: "pnr",
    cancelled: false,
    departure: actualTime(
      flightData.actualDepartureUtc,
      segment.departureAt,
      segment.from,
      flightData.reportedDepartureDelayMinutes,
      reference,
    ),
    arrival: actualTime(
      flightData.actualArrivalUtc,
      segment.arrivalAt,
      segment.to,
      flightData.reportedArrivalDelayMinutes,
      reference,
    ),
  };
}

export interface Replacements {
  /** Cancelled segment tattoo → the active flight that replaced it. */
  replacedBy: Map<string, PnrDisplaySegment>;
  /** Active segment tattoo → the cancelled flights it replaced. */
  replaces: Map<string, PnrDisplaySegment[]>;
}

const REPLACEMENT_WINDOW_MINUTES = 7 * 24 * 60;

const AMADEUS_MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

/** `DISRUPTION REBOOKED BY OPR FROM BA1488/08SEP26` → `BA1488|2026-09-08`. */
function rebookedFromKey(text: string): string | null {
  const match = text.match(/FROM ([A-Z0-9]{2})(\d{1,4})\/(\d{2})([A-Z]{3})(\d{2})/);
  if (!match) {
    return null;
  }

  const month = AMADEUS_MONTHS.indexOf(match[4]);
  if (month < 0) {
    return null;
  }

  return flightKey(match[1], match[2], `20${match[5]}-${String(month + 1).padStart(2, "0")}-${match[3]}`);
}

/**
 * Pairs each cancelled flight with the flight booked in its place, by strength of evidence: the
 * reprotection the `FE` endorsement points at, then a carrier `SSR OTHS … REBOOKED … FROM BA1488/08SEP26`
 * attached to the new segment, then the next active flight on the same route within a week.
 */
export function linkReplacements(
  groups: ItineraryGroups,
  view: Pick<PnrDisplayView, "rerouting" | "rawElements">,
): Replacements {
  const active = groups.journeys.flatMap((journey) => journey.segments);
  const replacedBy = new Map<string, PnrDisplaySegment>();
  const replaces = new Map<string, PnrDisplaySegment[]>();

  /** New segment tattoo → key of the flight the carrier says it was rebooked from. */
  const rebookedFrom = new Map<string, string>();
  for (const element of view.rawElements) {
    const key = element.name === "SSR" ? rebookedFromKey(element.text) : null;
    const tattoo = element.references.find((reference) => reference.startsWith("ST"))?.slice(2);
    if (key && tattoo) {
      rebookedFrom.set(tattoo, key);
    }
  }

  for (const cancelled of groups.cancelled) {
    const cancelledKey = segmentFlightKey(cancelled);
    const sameRoute = active.filter((flight) => flight.from === cancelled.from && flight.to === cancelled.to);
    const reprotection = sameRoute.find(
      (flight) => view.rerouting.detected && flight.tattoo === view.rerouting.reprotectionSegmentTattoo,
    );
    const declared = active.find(
      (flight) =>
        flight.tattoo !== null && cancelledKey !== null && rebookedFrom.get(flight.tattoo) === cancelledKey,
    );
    const next = sameRoute
      .map((flight) => ({ flight, gap: minutesBetween(cancelled.departureAt, flight.departureAt) }))
      .filter(({ gap }) => gap !== null && gap >= 0 && gap <= REPLACEMENT_WINDOW_MINUTES)
      .sort((a, b) => (a.gap ?? 0) - (b.gap ?? 0))[0]?.flight;
    const replacement = reprotection ?? declared ?? next;

    if (replacement && cancelled.tattoo && replacement.tattoo) {
      replacedBy.set(cancelled.tattoo, replacement);
      replaces.set(replacement.tattoo, [...(replaces.get(replacement.tattoo) ?? []), cancelled]);
    }
  }

  return { replacedBy, replaces };
}

export type ItineraryItem =
  | { kind: "journey"; journey: Journey; stay: Stay | null }
  | { kind: "cancelled"; segment: PnrDisplaySegment; label: string };

export interface ItinerarySequence {
  tripType: TripType;
  items: ItineraryItem[];
}

/**
 * Journeys and the cancelled flights nothing replaced, in the order they were meant to fly. A
 * dropped flight still is a leg of the trip: an outbound cancelled on the 14th makes the flight of
 * the 16th a « Retour », not an « Aller simple » — so the labels and trip type count it too.
 */
export function sequenceItinerary(groups: ItineraryGroups, orphans: PnrDisplaySegment[]): ItinerarySequence {
  const entries = [
    ...groups.journeys.map((journey, index) => ({
      at: journey.departureAt ?? "",
      leg: { from: journey.from, to: journey.to },
      item: { kind: "journey", journey, stay: groups.stays[index] ?? null } as ItineraryItem,
    })),
    ...orphans.map((segment) => ({
      at: segment.departureAt ?? "",
      leg: { from: segment.from, to: segment.to },
      item: { kind: "cancelled", segment, label: "" } as ItineraryItem,
    })),
  ].sort((a, b) => a.at.localeCompare(b.at));

  const legs = entries.map((entry) => entry.leg);
  const tripType = tripTypeOf(legs);
  const labels = journeyLabels(legs, tripType);

  return {
    tripType,
    items: entries.map(
      ({ item }, index): ItineraryItem =>
        item.kind === "journey"
          ? { ...item, journey: { ...item.journey, label: labels[index] } }
          : { ...item, label: labels[index] },
    ),
  };
}

export interface ReroutingDeltas {
  /** Replacement departure against the dropped flight's departure, same airport so exact. */
  departureMinutes: number | null;
  /** Arrival at the final destination against the dropped flight's planned arrival. */
  arrivalMinutes: number | null;
  arrivalIsActual: boolean;
  /** The replacement's arrival clock used for the comparison. */
  arrivalLocal: string;
}

/**
 * What the rerouting cost the passenger: how much later they left, and how much later they landed
 * at destination — measured on the replacement's actual arrival when it is known, its schedule
 * otherwise. The arrival delta is the figure eligibility turns on.
 */
export function reroutingDeltas(
  former: PnrDisplaySegment,
  replacement: PnrDisplaySegment,
  reference: PnrReference,
): ReroutingDeltas {
  const actual = actualTimes(replacement, reference)?.arrival ?? null;
  const zone = former.to ? reference.airports[former.to]?.timezone : null;
  const plannedArrival = former.arrivalAt && zone ? DateTime.fromISO(former.arrivalAt, { zone }) : null;

  if (actual && !actual.isUtc && plannedArrival?.isValid) {
    return {
      departureMinutes: minutesBetween(former.departureAt, replacement.departureAt),
      arrivalMinutes: Math.round(DateTime.fromISO(actual.at).diff(plannedArrival, "minutes").minutes),
      arrivalIsActual: true,
      arrivalLocal: actual.local,
    };
  }

  return {
    departureMinutes: minutesBetween(former.departureAt, replacement.departureAt),
    arrivalMinutes: minutesBetween(former.arrivalAt, replacement.arrivalAt),
    arrivalIsActual: false,
    arrivalLocal: formatNaiveTime(replacement.arrivalAt),
  };
}

/** `SSR notifié le 9 septembre 18:40` — the first timestamped carrier line attached to the segment. */
export function cancellationNotice(
  segment: PnrDisplaySegment,
  rawElements: PnrDisplayView["rawElements"],
): { at: string; source: string } | null {
  for (const element of rawElements) {
    if (element.name !== "SSR" && element.name !== "RM") {
      continue;
    }
    const attached = segment.tattoo !== null && element.references.includes(`ST${segment.tattoo}`);
    const names = element.text.includes(`${segment.carrier ?? ""}${segment.flightNumber ?? ""}`);
    const stamp = element.text.match(/(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/);
    if ((attached || names) && stamp) {
      return { at: `${stamp[1]}T${stamp[2]}:${stamp[3]}`, source: element.name };
    }
  }

  return null;
}
